import {
  Box,
  Flex,
  Text,
  VStack,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';

import type { QuoteSummaryVehicle, TaxFee } from 'api/quote-marketplace';
import type { ParsedVehicleData } from '../../utils/parseVehicleHtml';
import { formatCurrency } from '../../constants';
import { calculatePremiumBreakdown } from '../../utils/premiumCalculations';

interface VehicleSummaryItem {
  vehicle: QuoteSummaryVehicle;
  parsedData: ParsedVehicleData;
}

interface VehicleSummarySectionProps {
  vehicles: VehicleSummaryItem[];
  annualPremium: string | null;
  taxes: TaxFee[] | null;
}

const VehicleSummarySection = ({
  vehicles,
  annualPremium,
  taxes,
}: VehicleSummarySectionProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  const {
    annualPremium: premiumNum,
    taxTotal,
    taxPercentage,
  } = calculatePremiumBreakdown(annualPremium, taxes);

  return (
    <Box mb={6}>
      <Flex
        bg={headerBg}
        px={3}
        py={2}
        borderWidth="1px"
        borderColor={borderColor}
        borderTopRadius="md"
        justify="space-between"
      >
        <Text fontWeight="600" fontSize="sm">
          Vehicles
        </Text>
        <Text fontWeight="600" fontSize="sm">
          Totals
        </Text>
      </Flex>

      <Box
        borderWidth="1px"
        borderTopWidth="0"
        borderColor={borderColor}
        borderBottomRadius="md"
        p={3}
      >
        <VStack spacing={2} align="stretch">
          {vehicles.map(({ vehicle, parsedData }, index) => (
            <Flex key={vehicle.id} justify="space-between" align="center">
              <Text fontSize="sm" color="gray.700">
                Private Passenger - {vehicle.vehicle_model_year || ''}{' '}
                {vehicle.make?.toUpperCase()} {vehicle.model?.toUpperCase()} (
                {vehicle.vin || 'No VIN'})
              </Text>
              <Text fontSize="sm" fontWeight="500">
                {parsedData.totalPremium != null
                  ? formatCurrency(String(parsedData.totalPremium))
                  : '-'}
              </Text>
            </Flex>
          ))}

          <Flex
            justify="space-between"
            align="center"
            pt={3}
            mt={2}
            borderTopWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color="gray.600">
              Estimated Annual Premium
            </Text>
            <Text fontSize="sm">{formatCurrency(annualPremium)}</Text>
          </Flex>
          <Divider variant="dashed" />
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">
              {taxPercentage}% Tax Applied
            </Text>
            <Text fontSize="sm">{formatCurrency(String(taxTotal))}</Text>
          </Flex>

          <Flex
            justify="space-between"
            align="center"
            pt={3}
            mt={2}
            borderTopWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="sm" fontWeight="600">
              Estimated Total Premium
            </Text>
            <Text fontSize="sm" fontWeight="600">
              {formatCurrency(String(premiumNum))}
            </Text>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default VehicleSummarySection;
